import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { PlayingCard } from "../PlayingCard";
import { useDesktop } from "../../lib/desktop";
import { haptic, sound } from "../../lib/feedback";

/**
 * A completed set breaks when the turn is validated (owner's request, November 2026): on the server it is off the
 * table and under the discard pile at once (`_break_completed_sets`, `game.broken_sets`); here every player sees it
 * happen. The group stays in its place on the table, so nothing jumps, while the discard lands; it cracks, its cards
 * burst apart, then slip one by one under the pile, whose top card stays the card just discarded. The group's room is
 * given back at the end. A page opened later plays nothing: the event was before its time.
 */

const WAIT_MS = 450; // the discard that validated the turn lands first
const CRACK_S = 0.5;
const BURST_S = 0.3;
const FLIGHT_S = 0.55;
const SLIP_S = 0.22;
const STAGGER_S = 0.09;

const box = (el) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
};

// Where the cards are now, on this player's screen, and where they go. Null when there is nothing to animate from.
function measure(sets) {
  const pile = box(document.querySelector('[data-testid="discard-top"]'));
  if (!pile) return null;
  const groups = [];
  const cards = [];
  sets.forEach((set) => {
    const row = document.querySelector(`[data-breaking="${set.combo_id}"]`);
    const group = box(row);
    if (!group) return;
    groups.push({ id: set.combo_id, ...group });
    const shown = [...row.querySelectorAll("[data-break-card]")];
    shown.forEach((el, i) => {
      const card = set.combo.cards.find((c) => c.id === el.getAttribute("data-break-card"));
      const at = box(el);
      if (!card || !at) return;
      const side = i - (shown.length - 1) / 2;
      cards.push({
        card,
        at,
        // Past the crack, the cards spread: out from the middle, one up, one down.
        burst: { x: side * 18, y: i % 2 ? 8 : -16, rotate: side * 11 },
        to: { x: pile.x + pile.w / 2 - (at.x + at.w / 2), y: pile.y + pile.h / 2 - (at.y + at.h / 2) },
        scale: pile.w / at.w,
      });
    });
  });
  return cards.length ? { groups, cards, pile } : null;
}

export function useBrokenSets(game) {
  const reduceMotion = useReducedMotion();
  const event = game?.broken_sets || null;
  const seq = event?.seq ?? 0;
  const loaded = !!game;
  const [doneSeq, setDoneSeq] = useState(null); // null until the first state: nothing older than the page plays
  const [stage, setStage] = useState("wait"); // wait → crack → gone
  const [run, setRun] = useState(null);

  useEffect(() => {
    if (loaded && doneSeq === null) setDoneSeq(seq);
  }, [loaded, doneSeq, seq]);

  const active = doneSeq !== null && seq > doneSeq;

  useEffect(() => {
    if (!active) return undefined;
    if (reduceMotion || document.hidden) {
      setDoneSeq(seq);
      return undefined;
    }
    const timers = [];
    const after = (ms, fn) => timers.push(setTimeout(fn, ms));
    setStage("wait");
    after(WAIT_MS, () => {
      const measured = measure(event.sets);
      if (!measured) {
        setDoneSeq(seq);
        return;
      }
      // Over when the last card has slipped under the pile (a slow phone starts it late); the timer only covers
      // a page that stopped drawing meanwhile.
      const finish = () => {
        setRun(null);
        setStage("wait");
        setDoneSeq(seq);
      };
      setRun({ ...measured, finish });
      setStage("crack");
      sound.combo();
      haptic.light();
      after(CRACK_S * 1000, () => setStage("gone"));
      after((CRACK_S + BURST_S) * 1000, () => sound.cardFly());
      const lastCard = (measured.cards.length - 1) * STAGGER_S;
      after((CRACK_S + BURST_S + FLIGHT_S + SLIP_S + lastCard) * 1000 + 1500, finish);
    });
    return () => {
      timers.forEach(clearTimeout);
      setRun(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seq, reduceMotion]);

  // What the table shows: the groups the server has already taken off stay in their place until they have left.
  const tableGame = useMemo(() => {
    if (!active || !game) return game;
    return {
      ...game,
      players: game.players.map((p) => {
        const mine = event.sets.filter((s) => s.player_id === p.user_id);
        if (!mine.length) return p;
        const combinations = [...(p.combinations || [])];
        mine
          .slice()
          .sort((a, b) => a.index - b.index)
          .forEach((s) => combinations.splice(Math.min(s.index, combinations.length), 0, { ...s.combo, breaking: stage }));
        return { ...p, combinations };
      }),
    };
  }, [game, active, event, stage]);

  return { tableGame, run };
}

// The crack: a line of light across the group, under a flash.
function Crack({ group }) {
  const { w, h } = group;
  const line = [
    [0, 0.52], [0.16, 0.34], [0.29, 0.64], [0.44, 0.38], [0.58, 0.68], [0.74, 0.36], [0.88, 0.58], [1, 0.46],
  ].map(([x, y], i) => `${i ? "L" : "M"}${x * w},${y * h}`).join(" ");
  return (
    <div className="fixed" style={{ left: group.x, top: group.y, width: w, height: h }}>
      <motion.div
        className="absolute inset-0 rounded-lg bg-amber-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.32, 0] }}
        transition={{ duration: CRACK_S, times: [0, 0.45, 1] }}
      />
      <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
        <motion.path
          d={line}
          fill="none"
          stroke="#fef3c7"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 5px rgba(251,191,36,0.95))" }}
          initial={{ pathLength: 0, opacity: 1 }}
          animate={{ pathLength: 1, opacity: [1, 1, 0] }}
          transition={{
            pathLength: { duration: 0.2, delay: 0.1, ease: "easeOut" },
            opacity: { duration: CRACK_S, times: [0, 0.75, 1] },
          }}
        />
      </svg>
    </div>
  );
}

function BrokenCard({ item, index, pileHeight, size, onArrived }) {
  const total = CRACK_S + BURST_S + FLIGHT_S + index * STAGGER_S + SLIP_S;
  const at = (s) => s / total;
  // It comes in from below the pile and slips up under its top card (drawn over it, in the layer).
  const below = pileHeight * 0.62;
  return (
    <motion.div
      className="fixed"
      data-testid="broken-card"
      data-card={item.card.id}
      style={{ left: item.at.x, top: item.at.y, width: item.at.w, height: item.at.h, willChange: "transform" }}
      initial={{ x: 0, y: 0, rotate: 0, scale: 1 }}
      animate={{
        x: [0, 0, item.burst.x, item.to.x, item.to.x],
        y: [0, 0, item.burst.y, item.to.y + below, item.to.y],
        rotate: [0, 0, item.burst.rotate, item.burst.rotate * 0.4, 0],
        scale: [1, 1, 1.06, item.scale, item.scale],
      }}
      transition={{
        duration: total,
        times: [0, at(CRACK_S), at(CRACK_S + BURST_S), at(total - SLIP_S), 1],
        ease: ["linear", "easeOut", "easeInOut", "easeOut"],
      }}
      onAnimationComplete={onArrived}
    >
      <motion.div
        animate={{ x: [0, -2, 2, -1.5, 1.5, -1, 1, 0], rotate: [0, -1, 1, -1, 1, 0, 0, 0] }}
        transition={{ duration: CRACK_S }}
        style={{ lineHeight: 0 }}
      >
        <PlayingCard card={item.card} size={size} disabled readOnly />
      </motion.div>
    </motion.div>
  );
}

// Over the player's bar and the vote's dim light (z-40), under the end-of-round windows (z-50). Nothing takes a tap.
export function BrokenSetsLayer({ run, topCard, pileSize = "md-5" }) {
  const big = useDesktop();
  if (!run) return null;
  return createPortal(
    <div className="fixed inset-0 z-[45] pointer-events-none" aria-hidden="true" data-testid="broken-set-layer">
      {run.groups.map((group) => <Crack key={group.id} group={group} />)}
      {run.cards.map((item, i) => (
        <BrokenCard
          key={item.card.id}
          item={item}
          index={i}
          pileHeight={run.pile.h}
          size={big ? "xs-lg" : "xs"}
          onArrived={i === run.cards.length - 1 ? run.finish : undefined}
        />
      ))}
      {/* The pile's top card, over the cards slipping under it: the card just discarded stays on top. */}
      {topCard && (
        <div className="fixed" style={{ left: run.pile.x, top: run.pile.y, lineHeight: 0 }} data-testid="broken-set-top">
          <PlayingCard card={topCard} size={pileSize} disabled readOnly />
        </div>
      )}
    </div>,
    document.body,
  );
}
